import { useEffect, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Layers, Users, FileText, Sparkles, DollarSign } from "lucide-react";
import {
  Card as CardRoot,
  CardContent as CardContentRoot,
  CardHeader as CardHeaderRoot,
  CardTitle as CardTitleRoot,
  CardDescription as CardDescriptionRoot,
} from "@/components/ui/card";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

function fmt(c: number) {
  return `${c.toLocaleString("en-US")} cr`;
}

type CostSettings = {
  monthUsageCents: number;
  monthGiftedAllowanceCents: number;
  paidRemainingCents: number;
};

type AIUsageBreakdown = {
  features: { feature: string; costCents: number; calls: number }[];
};

type BookingSettings = {
  runsEnabled: boolean;
  multiTalentEnabled: boolean;
  salesRepCanEditCallSheets: boolean;
  // Task #611 — workspace-level shared-pipeline toggle. When on, sales reps
  // see and act on every lead/client (not just the ones they own).
  shareLeadsAcrossReps: boolean;
  aiCostsPaidBy: "workspace" | "talent" | "hybrid";
  aiHybridSubsidyCentsPerTalent: number | null;
  duesDefaultMonthlyCents: number | null;
  duesDefaultPercentOfBooking: string | null;
  duesDefaultOnboardingFeeCents: number | null;
};

function dollarsToCents(s: string): number | null {
  const n = parseFloat(s);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100);
}
function centsToDollars(c: number | null): string {
  if (c == null) return "";
  return (c / 100).toFixed(2);
}

export function BookingSettingsCard() {
  const { toast } = useToast();
  const { data, isLoading } = useQuery<BookingSettings>({
    queryKey: ["/api/admin/booking-settings"],
  });

  const [runsEnabled, setRunsEnabled] = useState(false);
  const [multiTalentEnabled, setMultiTalentEnabled] = useState(false);
  const [salesRepCanEdit, setSalesRepCanEdit] = useState(false);
  const [shareLeadsAcrossReps, setShareLeadsAcrossReps] = useState(false);
  const [duesMonthly, setDuesMonthly] = useState<string>("");
  const [duesPct, setDuesPct] = useState<string>("");
  const [duesOnboarding, setDuesOnboarding] = useState<string>("");
  const [advancedOpen, setAdvancedOpen] = useState(false);

  useEffect(() => {
    if (!data) return;
    setRunsEnabled(!!data.runsEnabled);
    setMultiTalentEnabled(!!data.multiTalentEnabled);
    setSalesRepCanEdit(!!data.salesRepCanEditCallSheets);
    setShareLeadsAcrossReps(!!data.shareLeadsAcrossReps);
    setDuesMonthly(centsToDollars(data.duesDefaultMonthlyCents));
    setDuesPct(data.duesDefaultPercentOfBooking ?? "");
    setDuesOnboarding(centsToDollars(data.duesDefaultOnboardingFeeCents));
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: async (payload: any) => {
      const res = await apiRequest("PATCH", "/api/admin/booking-settings", payload);
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Saved", description: "Booking settings updated." });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/booking-settings"] });
    },
    onError: (err: any) =>
      toast({
        title: "Save failed",
        description: err?.message ?? "Try again.",
        variant: "destructive",
      }),
  });

  const handleSave = () => {
    // NOTE: aiCostsPaidBy / aiHybridSubsidyCentsPerTalent are now edited
    // from the AI Settings panel (AiCostRoutingCard below). We intentionally
    // omit them from this payload so saving booking toggles never clobbers
    // a value the admin set on the other screen. The PATCH handler only
    // updates fields present in the body.
    saveMutation.mutate({
      runsEnabled,
      multiTalentEnabled,
      salesRepCanEditCallSheets: salesRepCanEdit,
      shareLeadsAcrossReps,
      duesDefaultMonthlyCents: dollarsToCents(duesMonthly),
      duesDefaultPercentOfBooking: duesPct.trim() === "" ? null : duesPct.trim(),
      duesDefaultOnboardingFeeCents: dollarsToCents(duesOnboarding),
    });
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-8 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="card-booking-settings">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Layers className="w-5 h-5" />
          Bookings & Multi-Talent
        </CardTitle>
        <CardDescription>
          Turn on multi-show runs, multi-talent shows, AI cost routing, and
          default talent dues. New booking dialogs reflect these toggles
          immediately.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Run runs */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <Label className="flex items-center gap-2 text-base">
              <FileText className="w-4 h-4" />
              Multi-show runs
            </Label>
            <p className="text-sm text-muted-foreground mt-1">
              Group multiple shows under one contract with shared deposits and
              cancellation terms. Hidden in the booking dialog when off.
            </p>
          </div>
          <Switch
            checked={runsEnabled}
            onCheckedChange={setRunsEnabled}
            data-testid="switch-runs-enabled"
          />
        </div>

        {/* Multi-talent */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <Label className="flex items-center gap-2 text-base">
              <Users className="w-4 h-4" />
              Multi-talent shows
            </Label>
            <p className="text-sm text-muted-foreground mt-1">
              Allow multiple talent on the same date with role + payout per
              talent. The booking still has one primary talent for routing.
            </p>
          </div>
          <Switch
            checked={multiTalentEnabled}
            onCheckedChange={setMultiTalentEnabled}
            data-testid="switch-multi-talent"
          />
        </div>

        {/* Sales rep call sheet edit */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <Label className="text-base">Sales reps can edit call sheets</Label>
            <p className="text-sm text-muted-foreground mt-1">
              When off, only the assigned artist (and admins) can edit a show's
              call sheet. Reps can always view.
            </p>
          </div>
          <Switch
            checked={salesRepCanEdit}
            onCheckedChange={setSalesRepCanEdit}
            data-testid="switch-rep-edit-call-sheets"
          />
        </div>

        {/* Share leads across reps — Task #611 */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <Label className="text-base">Share leads & clients across reps</Label>
            <p className="text-sm text-muted-foreground mt-1">
              When on, every sales rep can see and work every lead and client
              in the workspace (no per-rep ownership). Useful for shared
              pipelines and tour outreach. Off = each rep only sees their own.
            </p>
          </div>
          <Switch
            checked={shareLeadsAcrossReps}
            onCheckedChange={setShareLeadsAcrossReps}
            data-testid="switch-share-leads-across-reps"
          />
        </div>

        {/* Dues defaults — collapsed advanced */}
        <div className="pt-4 border-t">
          <button
            type="button"
            className="text-sm font-medium underline-offset-2 hover:underline"
            onClick={() => setAdvancedOpen((o) => !o)}
            data-testid="button-toggle-dues-advanced"
          >
            {advancedOpen ? "▾" : "▸"} Default talent dues (advanced)
          </button>
          <p className="text-sm text-muted-foreground mt-1">
            Defaults that pre-fill on new talent profiles. Each talent can be
            customized later in their billing tab.
          </p>
          {advancedOpen && (
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-sm">Monthly dues ($)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={duesMonthly}
                  onChange={(e) => setDuesMonthly(e.target.value)}
                  data-testid="input-dues-monthly"
                />
              </div>
              <div>
                <Label className="text-sm">% of each booking</Label>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={duesPct}
                  onChange={(e) => setDuesPct(e.target.value)}
                  placeholder="e.g. 5.00"
                  data-testid="input-dues-percent"
                />
              </div>
              <div>
                <Label className="text-sm">One-time onboarding fee ($)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={duesOnboarding}
                  onChange={(e) => setDuesOnboarding(e.target.value)}
                  data-testid="input-dues-onboarding"
                />
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end pt-2">
          <Button
            onClick={handleSave}
            disabled={saveMutation.isPending}
            data-testid="button-save-booking-settings"
          >
            {saveMutation.isPending && (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            )}
            Save changes
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * AI cost-routing card — lives on the AI Settings panel.
 *
 * Reads/writes the same `/api/admin/booking-settings` payload as
 * BookingSettingsCard (the field lives there in the schema), but is
 * surfaced in the AI Settings page so admins find it where they expect
 * AI billing controls. The two cards intentionally save independently
 * so editing one never overwrites the other.
 */
export function AiCostRoutingCard() {
  const { toast } = useToast();

  const { data: costData, isLoading: costLoading } = useQuery<CostSettings>({
    queryKey: ["/api/admin/ai-settings"],
  });
  const { data: breakdown } = useQuery<AIUsageBreakdown>({
    queryKey: ["/api/admin/ai-usage-breakdown"],
  });
  const { data, isLoading } = useQuery<BookingSettings>({
    queryKey: ["/api/admin/booking-settings"],
  });

  const [aiCostsPaidBy, setAiCostsPaidBy] = useState<
    "workspace" | "talent" | "hybrid"
  >("workspace");
  const [aiSubsidyDollars, setAiSubsidyDollars] = useState<string>("");
  const [capDollars, setCapDollars] = useState("0.00");

  useEffect(() => {
    if (!data) return;
    setAiCostsPaidBy(data.aiCostsPaidBy ?? "workspace");
    setAiSubsidyDollars(centsToDollars(data.aiHybridSubsidyCentsPerTalent));
  }, [data]);

  useEffect(() => {
    if (costData) setCapDollars((costData.aiOverageCapCents / 100).toFixed(2));
  }, [costData]);

  const saveCapMutation = useMutation({
    mutationFn: async () =>
      apiRequest("PATCH", "/api/admin/ai-settings", {
        aiOverageCapCents: Math.max(0, Math.round(parseFloat(capDollars || "0") * 100)),
      }),
    onSuccess: () => {
      toast({ title: "Saved", description: "Monthly overage cap updated." });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/ai-settings"] });
    },
    onError: (e: any) =>
      toast({ title: "Save failed", description: e?.message, variant: "destructive" }),
  });

  const saveMutation = useMutation({
    mutationFn: async (payload: any) => {
      const res = await apiRequest("PATCH", "/api/admin/booking-settings", payload);
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Saved", description: "AI cost routing updated." });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/booking-settings"] });
    },
    onError: (err: any) =>
      toast({
        title: "Save failed",
        description: err?.message ?? "Try again.",
        variant: "destructive",
      }),
  });

  const handleSave = () => {
    saveMutation.mutate({
      aiCostsPaidBy,
      aiHybridSubsidyCentsPerTalent:
        aiCostsPaidBy === "hybrid" ? dollarsToCents(aiSubsidyDollars) : null,
    });
  };

  return (
    <>
      {/* ── This month's spend ── */}
      <CardRoot data-testid="card-ai-spend-summary">
        <CardHeaderRoot>
          <CardTitleRoot className="flex items-center gap-2">
            <Sparkles className="w-5 h-5" />
            This month's spend
          </CardTitleRoot>
          <CardDescriptionRoot>
            AI credit usage for the current billing period. Help Assistant (helpbot) is platform-absorbed and excluded from workspace charges.
          </CardDescriptionRoot>
        </CardHeaderRoot>
        <CardContentRoot className="space-y-4">
          {costLoading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : costData ? (
            <>
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div className="rounded-md border p-3">
                  <div className="text-xs text-muted-foreground">Used this month</div>
                  <div className="text-base font-semibold" data-testid="text-ai-used">{fmt(costData.monthUsageCents)}</div>
                </div>
                <div className="rounded-md border p-3">
                  <div className="text-xs text-muted-foreground">Monthly gifted</div>
                  <div className="text-base font-semibold" data-testid="text-ai-gifted">{fmt(costData.monthGiftedAllowanceCents)}</div>
                </div>
                <div className="rounded-md border p-3">
                  <div className="text-xs text-muted-foreground">Paid balance</div>
                  <div className="text-base font-semibold" data-testid="text-ai-paid">{fmt(costData.paidRemainingCents)}</div>
                </div>
              </div>

              {/* ── Feature breakdown ── */}
              <div className="rounded-md border p-3" data-testid="ai-usage-breakdown">
                <div className="text-xs font-medium text-muted-foreground mb-3">
                  Spending by feature this month
                </div>
                {breakdown && breakdown.features.length > 0 ? (
                  <div className="space-y-2">
                    {breakdown.features.map((f) => (
                      <div
                        key={f.feature}
                        className="flex items-center justify-between text-sm"
                        data-testid={`row-ai-usage-${f.feature.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        <span className="text-foreground">{f.feature}</span>
                        <div className="flex items-center gap-3 text-right">
                          <span className="text-xs text-muted-foreground tabular-nums">
                            {f.calls.toLocaleString()} call{f.calls === 1 ? "" : "s"}
                          </span>
                          <span className="font-medium tabular-nums w-16 text-right">
                            {fmt(f.costCents)}
                          </span>
                        </div>
                      </div>
                    ))}
                    <div className="flex items-center justify-between text-sm border-t pt-2 mt-2">
                      <span className="font-medium">Total</span>
                      <span className="font-semibold tabular-nums w-16 text-right">
                        {fmt(breakdown.features.reduce((s, f) => s + f.costCents, 0))}
                      </span>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No usage recorded this month.</p>
                )}
              </div>

            </>
          ) : null}
        </CardContentRoot>
      </CardRoot>

      {/* ── Who pays for AI usage? ── */}
      <CardRoot data-testid="card-ai-cost-routing">
        <CardHeaderRoot>
          <CardTitleRoot className="flex items-center gap-2">
            <Sparkles className="w-5 h-5" />
            Who pays for AI usage?
          </CardTitleRoot>
          <CardDescriptionRoot>
            Decide whether the workspace eats AI cost, each talent pays from
            their own balance, or you subsidize a monthly amount per talent.
          </CardDescriptionRoot>
        </CardHeaderRoot>
        <CardContentRoot className="space-y-3">
          {isLoading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              <Select
                value={aiCostsPaidBy}
                onValueChange={(v) =>
                  setAiCostsPaidBy(v as "workspace" | "talent" | "hybrid")
                }
              >
                <SelectTrigger data-testid="select-ai-costs-paid-by">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="workspace">
                    Workspace pays (artists never charged)
                  </SelectItem>
                  <SelectItem value="talent">
                    Each talent pays via their own balance
                  </SelectItem>
                  <SelectItem value="hybrid">
                    Hybrid — workspace subsidizes a monthly amount per talent
                  </SelectItem>
                </SelectContent>
              </Select>
              {aiCostsPaidBy === "hybrid" && (
                <div>
                  <Label className="text-sm">Monthly subsidy per talent ($)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={aiSubsidyDollars}
                    onChange={(e) => setAiSubsidyDollars(e.target.value)}
                    placeholder="e.g. 25.00"
                    data-testid="input-ai-subsidy"
                  />
                </div>
              )}
              <div className="flex justify-end pt-2">
                <Button
                  onClick={handleSave}
                  disabled={saveMutation.isPending}
                  data-testid="button-save-ai-cost-routing"
                >
                  {saveMutation.isPending && (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  )}
                  Save changes
                </Button>
              </div>
            </>
          )}
        </CardContentRoot>
      </CardRoot>

      {/* ── Monthly overage cap ── */}
      <CardRoot data-testid="card-ai-overage-cap">
        <CardHeaderRoot>
          <CardTitleRoot className="flex items-center gap-2">
            <DollarSign className="w-5 h-5" />
            Monthly overage cap
          </CardTitleRoot>
          <CardDescriptionRoot>
            Hard ceiling for paid usage above the gifted allowance. Set to 0 to allow only gifted credits.
          </CardDescriptionRoot>
        </CardHeaderRoot>
        <CardContentRoot className="space-y-3">
          <div>
            <Label htmlFor="ai-cap">Cap amount ($)</Label>
            <Input
              id="ai-cap"
              type="number"
              min={0}
              step="0.01"
              value={capDollars}
              onChange={(e) => setCapDollars(e.target.value)}
              data-testid="input-ai-cap"
            />
          </div>
          <div className="flex justify-end">
            <Button
              onClick={() => saveCapMutation.mutate()}
              disabled={saveCapMutation.isPending}
              data-testid="button-save-ai-cap"
            >
              {saveCapMutation.isPending && (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              )}
              Save cap
            </Button>
          </div>
        </CardContentRoot>
      </CardRoot>
    </>
  );
}
